import React, { useMemo } from 'react';
import {
  Box,
  Typography,
  CircularProgress,
  Chip,
  Divider,
  alpha,
  Skeleton,
  Tooltip,
} from '@mui/material';
import CalculateIcon from '@mui/icons-material/Calculate';
import LocalOfferIcon from '@mui/icons-material/LocalOffer';
import InventoryIcon from '@mui/icons-material/Inventory';
import SavingsIcon from '@mui/icons-material/Savings';
import TrendingDownIcon from '@mui/icons-material/TrendingDown';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import { useQuery } from '@tanstack/react-query';
import axiosInstance from '../../../lib/axios';
import type { ResellerProfile, TierName } from '../../../types/b2b';

interface POSColors {
  bg: string;
  surface: string;
  surfaceLight: string;
  surfaceElevated?: string;
  border: string;
  textPrimary: string;
  textSecondary: string;
  textMuted: string;
  accent: string;
  accentLight: string;
  success?: string;
  warning?: string;
  error?: string;
}

interface CartItem {
  productId: string;
  productName: string;
  quantity: number;
  retailPrice: number;
}

interface B2BPricingItemResult {
  productId: string;
  productName: string;
  quantity: number;
  retailPrice: number;
  b2bBasePrice: number;
  tierName: TierName | null;
  tierDiscount: number;
  tierDiscountAmount: number;
  quantityDiscount: number;
  quantityDiscountAmount: number;
  finalUnitPrice: number;
  subtotal: number;
}

interface B2BPricingSummary {
  subtotal: number;
  totalTierDiscount: number;
  totalQuantityDiscount: number;
  totalSavings: number;
  savingsPercentage: number;
  grandTotal: number;
}

interface B2BPricingResponse {
  items: B2BPricingItemResult[];
  summary: B2BPricingSummary;
}

interface B2BPricingCalculatorProps {
  reseller: ResellerProfile | null;
  items: CartItem[];
  colors: POSColors;
  compact?: boolean;
  onPricingCalculated?: (pricing: B2BPricingResponse | null) => void;
}

// Get tier-specific color
const getTierColor = (tier: TierName | undefined | null): string => {
  switch (tier) {
    case 'bronze':
      return '#CD7F32';
    case 'silver':
      return '#C0C0C0';
    case 'gold':
      return '#FFD700';
    case 'platinum':
      return '#E5E4E2';
    case 'custom':
      return '#7C3AED';
    default:
      return '#9CA3AF';
  }
};

// Get tier display label
const getTierLabel = (tier: TierName | undefined | null): string => {
  if (!tier) return 'Standard';
  return tier.charAt(0).toUpperCase() + tier.slice(1);
};

export const B2BPricingCalculator: React.FC<B2BPricingCalculatorProps> = ({
  reseller,
  items,
  colors,
  compact = false,
  onPricingCalculated,
}) => {
  const successColor = colors.success || '#22C55E';
  const warningColor = colors.warning || '#F59E0B';

  // Build pricing request items
  const pricingRequestItems = useMemo(() => {
    return items.map((item) => ({
      productId: item.productId,
      quantity: item.quantity,
    }));
  }, [items]);

  // Fetch B2B pricing from API
  const {
    data: pricingData,
    isLoading,
    isError,
    error,
  } = useQuery<B2BPricingResponse>({
    queryKey: ['b2b-pricing', reseller?.id, pricingRequestItems],
    queryFn: async () => {
      if (!reseller || items.length === 0) {
        return {
          items: [],
          summary: {
            subtotal: 0,
            totalTierDiscount: 0,
            totalQuantityDiscount: 0,
            totalSavings: 0,
            savingsPercentage: 0,
            grandTotal: 0,
          },
        };
      }

      const response = await axiosInstance.post('/sales/b2b/calculate-price', {
        resellerId: reseller.id,
        items: pricingRequestItems,
      });

      return response.data;
    },
    enabled: !!reseller && items.length > 0,
    staleTime: 10000, // Cache for 10 seconds
  });

  // Notify parent of pricing changes
  React.useEffect(() => {
    if (onPricingCalculated) {
      onPricingCalculated(pricingData || null);
    }
  }, [pricingData, onPricingCalculated]);

  // Calculate retail total for comparison
  const retailTotal = useMemo(() => {
    return items.reduce((sum, item) => sum + item.retailPrice * item.quantity, 0);
  }, [items]);

  // Format price in DZD
  const formatPrice = (price: number): string => {
    return new Intl.NumberFormat('fr-DZ', {
      style: 'currency',
      currency: 'DZD',
      minimumFractionDigits: 0,
    }).format(price);
  };

  // Format percentage
  const formatPercent = (value: number): string => {
    return `${value.toFixed(1)}%`;
  };

  // No reseller selected state
  if (!reseller) {
    return (
      <Box
        sx={{
          p: 2,
          backgroundColor: alpha(colors.textMuted, 0.05),
          borderRadius: '12px',
          border: `1px dashed ${colors.border}`,
          textAlign: 'center',
        }}
      >
        <CalculateIcon sx={{ fontSize: 32, color: colors.textMuted, mb: 1 }} />
        <Typography
          sx={{
            fontSize: '13px',
            color: colors.textMuted,
            fontWeight: 500,
          }}
        >
          Select a reseller to see B2B pricing
        </Typography>
      </Box>
    );
  }

  // No items state
  if (items.length === 0) {
    return (
      <Box
        sx={{
          p: 2,
          backgroundColor: alpha(colors.textMuted, 0.05),
          borderRadius: '12px',
          border: `1px dashed ${colors.border}`,
          textAlign: 'center',
        }}
      >
        <InventoryIcon sx={{ fontSize: 32, color: colors.textMuted, mb: 1 }} />
        <Typography
          sx={{
            fontSize: '13px',
            color: colors.textMuted,
            fontWeight: 500,
          }}
        >
          Add items to calculate B2B pricing
        </Typography>
      </Box>
    );
  }

  // Loading state
  if (isLoading) {
    return (
      <Box
        sx={{
          p: 2,
          backgroundColor: colors.surface,
          borderRadius: '12px',
          border: `1px solid ${colors.border}`,
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 2 }}>
          <CircularProgress size={20} sx={{ color: colors.accent }} />
          <Typography
            sx={{
              fontSize: '13px',
              color: colors.textSecondary,
              fontWeight: 500,
            }}
          >
            Calculating B2B pricing...
          </Typography>
        </Box>
        <Skeleton variant="rectangular" height={60} sx={{ borderRadius: '8px', mb: 1.5 }} />
        <Skeleton variant="rectangular" height={40} sx={{ borderRadius: '8px' }} />
      </Box>
    );
  }

  // Error state
  if (isError) {
    return (
      <Box
        sx={{
          p: 2,
          backgroundColor: alpha(colors.error || '#EF4444', 0.1),
          borderRadius: '12px',
          border: `1px solid ${alpha(colors.error || '#EF4444', 0.3)}`,
        }}
      >
        <Typography
          sx={{
            fontSize: '13px',
            color: colors.error || '#EF4444',
            fontWeight: 500,
          }}
        >
          Failed to calculate pricing: {(error as Error)?.message || 'Unknown error'}
        </Typography>
      </Box>
    );
  }

  const { summary } = pricingData || {
    summary: {
      subtotal: 0,
      totalTierDiscount: 0,
      totalQuantityDiscount: 0,
      totalSavings: 0,
      savingsPercentage: 0,
      grandTotal: 0,
    },
  };

  const tierName = reseller.defaultTier?.tierName;
  const tierColor = getTierColor(tierName);

  return (
    <Box
      sx={{
        backgroundColor: colors.surface,
        borderRadius: '12px',
        border: `1px solid ${colors.border}`,
        overflow: 'hidden',
      }}
    >
      {/* Header */}
      <Box
        sx={{
          p: 2,
          backgroundColor: alpha(colors.accent, 0.1),
          borderBottom: `1px solid ${colors.border}`,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
          <CalculateIcon sx={{ color: colors.accent, fontSize: 20 }} />
          <Typography
            sx={{
              fontSize: '14px',
              fontWeight: 600,
              color: colors.textPrimary,
            }}
          >
            B2B Pricing Breakdown
          </Typography>
        </Box>
        {tierName && (
          <Chip
            label={getTierLabel(tierName)}
            size="small"
            sx={{
              height: '22px',
              fontSize: '11px',
              fontWeight: 600,
              backgroundColor: alpha(tierColor, 0.15),
              color: tierColor,
              border: `1px solid ${tierColor}`,
              '& .MuiChip-label': {
                px: 1,
              },
            }}
          />
        )}
      </Box>

      {/* Pricing Details */}
      <Box sx={{ p: 2 }}>
        {/* Retail vs B2B Comparison */}
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            mb: 1.5,
          }}
        >
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
            <Typography
              sx={{
                fontSize: '12px',
                color: colors.textMuted,
                fontWeight: 500,
              }}
            >
              Retail Price
            </Typography>
            <Tooltip title="Standard retail price without B2B discounts">
              <InfoOutlinedIcon sx={{ fontSize: 14, color: colors.textMuted }} />
            </Tooltip>
          </Box>
          <Typography
            sx={{
              fontSize: '13px',
              color: colors.textMuted,
              fontWeight: 500,
              textDecoration: 'line-through',
            }}
          >
            {formatPrice(retailTotal)}
          </Typography>
        </Box>

        {/* Tier Discount */}
        {summary.totalTierDiscount > 0 && (
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              mb: 1,
              py: 0.75,
              px: 1,
              backgroundColor: alpha(successColor, 0.08),
              borderRadius: '6px',
            }}
          >
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
              <LocalOfferIcon sx={{ fontSize: 16, color: successColor }} />
              <Typography
                sx={{
                  fontSize: '12px',
                  color: successColor,
                  fontWeight: 500,
                }}
              >
                {getTierLabel(tierName)} Tier Discount
              </Typography>
            </Box>
            <Typography
              sx={{
                fontSize: '13px',
                color: successColor,
                fontWeight: 600,
              }}
            >
              -{formatPrice(summary.totalTierDiscount)}
            </Typography>
          </Box>
        )}

        {/* Quantity Discount */}
        {summary.totalQuantityDiscount > 0 && (
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              mb: 1,
              py: 0.75,
              px: 1,
              backgroundColor: alpha(warningColor, 0.08),
              borderRadius: '6px',
            }}
          >
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
              <InventoryIcon sx={{ fontSize: 16, color: warningColor }} />
              <Typography
                sx={{
                  fontSize: '12px',
                  color: warningColor,
                  fontWeight: 500,
                }}
              >
                Quantity Discount
              </Typography>
            </Box>
            <Typography
              sx={{
                fontSize: '13px',
                color: warningColor,
                fontWeight: 600,
              }}
            >
              -{formatPrice(summary.totalQuantityDiscount)}
            </Typography>
          </Box>
        )}

        {!compact && pricingData && pricingData.items.length > 0 && (
          <>
            <Divider sx={{ my: 1.5, borderColor: colors.border }} />

            {/* Per-item breakdown (collapsed by default in compact mode) */}
            <Typography
              sx={{
                fontSize: '11px',
                fontWeight: 600,
                color: colors.textMuted,
                textTransform: 'uppercase',
                letterSpacing: '0.5px',
                mb: 1,
              }}
            >
              Item Details
            </Typography>
            <Box sx={{ maxHeight: 200, overflowY: 'auto' }}>
              {pricingData.items.map((item, index) => (
                <Box
                  key={item.productId}
                  sx={{
                    py: 1,
                    borderBottom:
                      index < pricingData.items.length - 1
                        ? `1px solid ${alpha(colors.border, 0.5)}`
                        : 'none',
                  }}
                >
                  <Box
                    sx={{
                      display: 'flex',
                      justifyContent: 'space-between',
                      alignItems: 'flex-start',
                      mb: 0.5,
                    }}
                  >
                    <Typography
                      sx={{
                        fontSize: '12px',
                        fontWeight: 500,
                        color: colors.textPrimary,
                        flex: 1,
                        pr: 1,
                      }}
                    >
                      {item.productName}
                    </Typography>
                    <Typography
                      sx={{
                        fontSize: '12px',
                        fontWeight: 600,
                        color: colors.textPrimary,
                      }}
                    >
                      {formatPrice(item.subtotal)}
                    </Typography>
                  </Box>
                  <Box
                    sx={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: 1,
                      flexWrap: 'wrap',
                    }}
                  >
                    <Typography
                      sx={{
                        fontSize: '10px',
                        color: colors.textMuted,
                      }}
                    >
                      {item.quantity} x {formatPrice(item.finalUnitPrice)}
                    </Typography>
                    {item.tierDiscount > 0 && (
                      <Chip
                        label={`-${formatPercent(item.tierDiscount)} tier`}
                        size="small"
                        sx={{
                          height: '16px',
                          fontSize: '9px',
                          backgroundColor: alpha(successColor, 0.1),
                          color: successColor,
                          '& .MuiChip-label': { px: 0.5 },
                        }}
                      />
                    )}
                    {item.quantityDiscount > 0 && (
                      <Chip
                        label={`-${formatPercent(item.quantityDiscount)} qty`}
                        size="small"
                        sx={{
                          height: '16px',
                          fontSize: '9px',
                          backgroundColor: alpha(warningColor, 0.1),
                          color: warningColor,
                          '& .MuiChip-label': { px: 0.5 },
                        }}
                      />
                    )}
                  </Box>
                </Box>
              ))}
            </Box>
          </>
        )}

        <Divider sx={{ my: 1.5, borderColor: colors.border }} />

        {/* Total Savings */}
        {summary.totalSavings > 0 && (
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              mb: 1.5,
              py: 1,
              px: 1.5,
              backgroundColor: alpha(successColor, 0.12),
              borderRadius: '8px',
              border: `1px solid ${alpha(successColor, 0.3)}`,
            }}
          >
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <SavingsIcon sx={{ fontSize: 18, color: successColor }} />
              <Box>
                <Typography
                  sx={{
                    fontSize: '12px',
                    color: successColor,
                    fontWeight: 600,
                  }}
                >
                  Total Savings
                </Typography>
                <Typography
                  sx={{
                    fontSize: '10px',
                    color: alpha(successColor, 0.8),
                  }}
                >
                  {formatPercent(summary.savingsPercentage)} off retail
                </Typography>
              </Box>
            </Box>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
              <TrendingDownIcon sx={{ fontSize: 16, color: successColor }} />
              <Typography
                sx={{
                  fontSize: '16px',
                  color: successColor,
                  fontWeight: 700,
                }}
              >
                {formatPrice(summary.totalSavings)}
              </Typography>
            </Box>
          </Box>
        )}

        {/* Grand Total */}
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            py: 1,
          }}
        >
          <Typography
            sx={{
              fontSize: '14px',
              fontWeight: 600,
              color: colors.textSecondary,
            }}
          >
            B2B Total
          </Typography>
          <Typography
            sx={{
              fontSize: '22px',
              fontWeight: 800,
              color: colors.textPrimary,
              letterSpacing: '-0.5px',
            }}
          >
            {formatPrice(summary.grandTotal)}
          </Typography>
        </Box>
      </Box>
    </Box>
  );
};

export default B2BPricingCalculator;
